<script lang="ts" setup>
import { ICommonButtonEmits, ICommonButtonProps } from "@/types/main";

const props = withDefaults(defineProps<ICommonButtonProps>(), {
  type: "primary",
  position: "horizontal",
  label: "",
  loading: false,
  iconOnly: false,
});

const emit = defineEmits<ICommonButtonEmits>();

const clickButton = () => {
  if (props.isDisabled || props.loading) return;
  emit("clicked");
};
</script>

<template>
  <div class="c-co-button" :class="{ 'icon-only': iconOnly }">
    <div
      class="body"
      :class="[type, isDisabled ? 'disabled' : '', loading ? 'loading' : '']"
      @click="clickButton"
    >
      <div v-if="loading" class="body__loading">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid"
          width="20"
          height="20"
          style="shape-rendering: auto; display: block"
        >
          <g>
            <g transform="matrix(1,0,0,1,0,0)">
              <rect
                fill="#ffffff"
                height="13"
                width="7"
                ry="6.5"
                rx="3.5"
                y="17.5"
                x="46.5"
                opacity="0.225001"
              />
            </g>
            <g
              transform="matrix(0.7071067811865476,0.7071067811865476,-0.7071067811865476,0.7071067811865476,50,-20.710678118654755)"
            >
              <rect
                fill="#ffffff"
                height="13"
                width="7"
                ry="6.5"
                rx="3.5"
                y="17.5"
                x="46.5"
                opacity="0.350001"
              />
            </g>
            <g
              transform="matrix(6.123233995736766e-17,1,-1,6.123233995736766e-17,100,0)"
            >
              <rect
                fill="#ffffff"
                height="13"
                width="7"
                ry="6.5"
                rx="3.5"
                y="17.5"
                x="46.5"
                opacity="0.475001"
              />
            </g>
            <g
              transform="matrix(-0.7071067811865475,0.7071067811865476,-0.7071067811865476,-0.7071067811865475,120.71067811865476,49.99999999999999)"
            >
              <rect
                fill="#ffffff"
                height="13"
                width="7"
                ry="6.5"
                rx="3.5"
                y="17.5"
                x="46.5"
                opacity="0.600001"
              />
            </g>
            <g
              transform="matrix(-1,1.2246467991473532e-16,-1.2246467991473532e-16,-1,100,100)"
            >
              <rect
                fill="#ffffff"
                height="13"
                width="7"
                ry="6.5"
                rx="3.5"
                y="17.5"
                x="46.5"
                opacity="0.725001"
              />
            </g>
            <g
              transform="matrix(-0.7071067811865477,-0.7071067811865475,0.7071067811865475,-0.7071067811865477,50.000000000000014,120.71067811865476)"
            >
              <rect
                fill="#ffffff"
                height="13"
                width="7"
                ry="6.5"
                rx="3.5"
                y="17.5"
                x="46.5"
                opacity="0.850001"
              />
            </g>
            <g
              transform="matrix(-1.8369701987210297e-16,-1,1,-1.8369701987210297e-16,7.105427357601002e-15,100)"
            >
              <rect
                fill="#ffffff"
                height="13"
                width="7"
                ry="6.5"
                rx="3.5"
                y="17.5"
                x="46.5"
                opacity="0.975001"
              />
            </g>
            <g
              transform="matrix(0.7071067811865474,-0.7071067811865477,0.7071067811865477,0.7071067811865474,-20.710678118654755,50.000000000000014)"
            >
              <rect
                fill="#ffffff"
                height="13"
                width="7"
                ry="6.5"
                rx="3.5"
                y="17.5"
                x="46.5"
                opacity="0.100001"
              />
            </g>
          </g>
        </svg>
      </div>

      <template v-else-if="iconOnly">
        <component :is="icon" v-if="icon" :size="20" />
        <span v-else>+</span>
      </template>

      <template v-else>
        <div v-if="prepend && prependIcon" class="prepend__icon">
          <component :is="prependIcon" :size="20" />
        </div>
        <div v-if="label" class="body__text">{{ label }}</div>
        <slot v-else></slot>
        <div v-if="append && appendIcon" class="append__icon">
          <component :is="appendIcon" :size="20" />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.c-co-button {
  display: inline-flex;

  .body {
    padding: 12px 24px;
    background: var(--color-primary);
    border-radius: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    color: var(--color-white);
    cursor: pointer;
    transition: opacity 0.15s ease;

    .prepend__icon {
      width: 20px;
      height: 20px;
      background: transparent;
    }

    &__text {
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0;
      font-weight: 700;
      background: transparent;
    }

    &__loading {
      svg {
        animation: spin 1s linear infinite;
        transform-origin: center;
        will-change: transform;
      }
    }

    .append__icon {
      width: 20px;
      height: 20px;
      background: transparent;
    }

    &.disabled {
      background: var(--color-label-secondary);
      cursor: not-allowed;
    }

    &.loading {
      cursor: not-allowed;
    }

    &.outline {
      background: var(--color-system-background);
      border: 1px solid var(--color-label);
      padding: 11px 23px;
      color: var(--color-label) !important;

      &.disabled {
        color: var(--color-label-secondary) !important;
        border-color: var(--color-label-secondary);
      }
    }

    &.outline-primary {
      background: var(--color-system-background);
      border: 1px solid var(--color-primary);
      padding: 11px 23px;
      color: var(--color-primary) !important;
      &.disabled {
        color: var(--color-label-secondary) !important;
        border-color: var(--color-label-secondary);
      }
    }

    &.secondary {
      background: var(--color-secondary-system-background);
      color: var(--color-label) !important;

      &.disabled {
        color: var(--color-label-secondary) !important;
      }
    }

    &.error {
      background: var(--color-system-background);
      border: 1px solid var(--color-system-red);
      padding: 11px 23px;
      color: var(--color-system-red) !important;

      &.disabled {
        color: var(--color-red-light) !important;
        border-color: var(--color-red-light);
      }
    }

    &.link {
      color: var(--color-label);
      background: var(--color-white);

      &.disabled {
        color: var(--color-label-secondary) !important;
      }
    }
  }

  &.icon-only {
    .body {
      padding: 12px;
      width: 44px;
      height: 44px;
      border-radius: 14px;

      &.outline,
      &.error {
        padding: 11px;
      }
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
